import { Device, Document, HARDCODED_TEMPLATE_KEY } from "@models";
import { TemplateConfig } from "@types";
import { optionsToChangeType } from "src/pages/assistant/DocumentUpdate/DU_Configure";
import { getFilteredElements } from "src/utils/documents";

type UpdatePlan = {
  dataKeyId: string;
  templateKey: string;
};

export const changeTypeToTemplates = {
  bugFix: [
    HARDCODED_TEMPLATE_KEY.BUG_FIX_REPORT,
    HARDCODED_TEMPLATE_KEY.LIST_OF_KNOWN_ANOMALIES,
    HARDCODED_TEMPLATE_KEY.UDI_CREATION,
    HARDCODED_TEMPLATE_KEY.DEVICE_LABEL,
    HARDCODED_TEMPLATE_KEY.LIST_OF_MEDICAL_DEVICES,
    HARDCODED_TEMPLATE_KEY.RELEASE_NOTES,
    HARDCODED_TEMPLATE_KEY.PRODUCT_RELEASE_CHECKLIST,
  ],
  patch: [
    HARDCODED_TEMPLATE_KEY.CHANGE_REQUEST,
    HARDCODED_TEMPLATE_KEY.SUBSYSTEM_REQUIREMENTS_LIST,
    HARDCODED_TEMPLATE_KEY.SOUP_LIST,
    HARDCODED_TEMPLATE_KEY.SOFTWARE_TEST_PLAN,
    HARDCODED_TEMPLATE_KEY.SOFTWARE_SYSTEM_TEST_REPORT,
    HARDCODED_TEMPLATE_KEY.DESIGN_REVIEW_CHECKLIST,
    HARDCODED_TEMPLATE_KEY.CHANGE_EVALUATION_REPORT,
    HARDCODED_TEMPLATE_KEY.LIST_OF_KNOWN_ANOMALIES,
    HARDCODED_TEMPLATE_KEY.UDI_CREATION,
    HARDCODED_TEMPLATE_KEY.DEVICE_LABEL,
    HARDCODED_TEMPLATE_KEY.LIST_OF_MEDICAL_DEVICES,
    HARDCODED_TEMPLATE_KEY.RELEASE_NOTES,
    HARDCODED_TEMPLATE_KEY.PRODUCT_RELEASE_CHECKLIST,
  ],
  minor: [
    HARDCODED_TEMPLATE_KEY.CHANGE_REQUEST,
    HARDCODED_TEMPLATE_KEY.SUBSYSTEM_REQUIREMENTS_LIST,
    HARDCODED_TEMPLATE_KEY.SOUP_LIST,
    HARDCODED_TEMPLATE_KEY.SOFTWARE_TEST_PLAN,
    HARDCODED_TEMPLATE_KEY.SOFTWARE_SYSTEM_TEST_REPORT,
    HARDCODED_TEMPLATE_KEY.RISK_ASSESSMENT,
    HARDCODED_TEMPLATE_KEY.DESIGN_REVIEW_CHECKLIST,
    HARDCODED_TEMPLATE_KEY.USABILITY_EVALUATION_PROTOCOL,
    HARDCODED_TEMPLATE_KEY.USABILITY_EVALUATION_REPORT,
    HARDCODED_TEMPLATE_KEY.CHANGE_EVALUATION_REPORT,
    HARDCODED_TEMPLATE_KEY.LIST_OF_KNOWN_ANOMALIES,
    HARDCODED_TEMPLATE_KEY.UDI_CREATION,
    HARDCODED_TEMPLATE_KEY.DEVICE_LABEL,
    HARDCODED_TEMPLATE_KEY.LIST_OF_MEDICAL_DEVICES,
    HARDCODED_TEMPLATE_KEY.RELEASE_NOTES,
    HARDCODED_TEMPLATE_KEY.PRODUCT_RELEASE_CHECKLIST,
  ],
  major: [],
};

export const getElementIdsFromTemplate = (
  template: string,
  templatesConfig: TemplateConfig
): string[] => {
  return templatesConfig[template].questions.map((question) => question.id);
};

export const getAllElementIdsFromTemplates = (
  templateKeys: string[],
  templatesConfig: TemplateConfig
): string[] => {
  return templateKeys.reduce((acc: string[], templateKey: string) => {
    const template = templatesConfig[templateKey];
    if (template) {
      const elementIds = getElementIdsFromTemplate(
        templateKey,
        templatesConfig
      );
      acc.push(...elementIds);
    }
    return acc;
  }, []);
};

export const getHighestChangeType = (
  selected: string[]
): keyof typeof changeTypeToTemplates => {
  const changeTypes = selected.map((option) => optionsToChangeType[option]);

  if (changeTypes.includes("major")) return "major";
  if (changeTypes.includes("minor")) return "minor";
  if (changeTypes.includes("patch")) return "patch";
  return "bugFix";
};

const getTemplatesForChangeType = (selected: string[]) => {
  // Get highest change type from selection
  const highestChangeType = getHighestChangeType(selected);

  // Only get templates for the highest change type
  const templates = changeTypeToTemplates[highestChangeType] || [];

  // Return highest change type templates plus common templates
  return [...new Set([...templates])];
};

export const getDataKeysToUpdate = (
  selectValue: string[],
  documents: Document[],
  device: Device,
  templatesConfig: TemplateConfig
) => {
  const templateKeys = getTemplatesForChangeType(selectValue);

  const filteredTemplateKeys = templateKeys.filter((templateKey) => {
    const templateConfig = templatesConfig[templateKey];

    const document = documents.find(
      (document) => document.templateKey === templateKey
    );

    if (templateConfig.hidden) {
      return false;
    }

    // We always include documents in the update plan that are in the templateKeys and exist
    if (document) {
      return true;
    }

    // We always include RCDs in the update plan
    if (templateConfig.regulatoryType === "RCD") {
      return true;
    }

    return false;
  });

  const dataKeysToUpdate = filteredTemplateKeys.reduce<UpdatePlan[]>(
    (acc, templateKey) => {
      const template = templatesConfig[templateKey];

      const visibleElements = getFilteredElements(
        template.questions,
        device,
        documents
      );

      visibleElements.forEach((element) => {
        acc.push({
          dataKeyId: element.id,
          templateKey: template.templateKey,
        });
      });

      return acc;
    },
    []
  );

  return dataKeysToUpdate;
};
